import React, { useState } from 'react';
import { useParticipation } from '../../hooks/useParticipation';
import Button from './Button';
import Card from './Card';
import LoadingSpinner from './LoadingSpinner';
import styles from './ParticipationGate.module.css';

/**
 * The "who may participate" gate. Renders `children` only once the server says the
 * user is permitted (an adult who has given their age, or a minor whose guardian
 * has consented). The server is authoritative — the CCS loop endpoints refuse a
 * non-permitted caller with 403 — so this component is the friendly front for a
 * rule enforced on the backend, never the enforcement itself.
 *
 * When a minor submits a guardian's details, the backend emails the guardian an
 * approval link (/guardian-approve?token=...); the learner sees a waiting state
 * until the guardian responds. The consent *version* is server-controlled
 * (`guardian_consent_version`); wording here is subject to final legal review.
 */
const CURRENT_YEAR = new Date().getFullYear();

export default function ParticipationGate({ children }) {
  const { state, submitBirthYear, requestGuardian, rejoin } = useParticipation();
  const [birthYear, setBirthYear] = useState('');
  const [guardian, setGuardian] = useState({ name: '', email: '', relationship: 'parent' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (state === 'loading') {
    return (
      <div className={styles.wrap}>
        <LoadingSpinner />
      </div>
    );
  }

  if (state === 'permitted') {
    return children;
  }

  const run = async (fn) => {
    setSubmitting(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Age not yet known — ask for birth year.
  if (state === 'blocked_pending_age' || state === 'error') {
    const yr = parseInt(birthYear, 10);
    const valid = yr >= 1900 && yr <= CURRENT_YEAR;
    return (
      <div className={styles.wrap}>
        <Card title="One quick thing before you start">
          <p className={styles.body}>
            To set things up correctly, tell us the year you were born.
          </p>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="birthYear">Year of birth</label>
            <input
              id="birthYear"
              className={styles.input}
              type="number"
              inputMode="numeric"
              min="1900"
              max={CURRENT_YEAR}
              placeholder="e.g. 1998"
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
            />
          </div>
          {state === 'error' && (
            <p className={styles.error}>We couldn&apos;t check your status. You can still continue below.</p>
          )}
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.actions}>
            <Button
              onClick={() => run(() => submitBirthYear(yr))}
              loading={submitting}
              disabled={submitting || !valid}
            >
              Continue
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Minor — needs a guardian to consent.
  if (state === 'blocked_pending_guardian') {
    const emailOk = /.+@.+\..+/.test(guardian.email);
    const canSend = guardian.name.trim() && emailOk && !submitting;
    return (
      <div className={styles.wrap}>
        <Card title="A parent or guardian needs to say yes first">
          <p className={styles.body}>
            Because you&apos;re under 18, we need a parent or guardian to give permission before you
            begin. Add their details and we&apos;ll send them a request to approve.
          </p>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="gName">Parent/guardian name</label>
            <input
              id="gName" className={styles.input} type="text" value={guardian.name}
              onChange={(e) => setGuardian((g) => ({ ...g, name: e.target.value }))}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="gEmail">Their email</label>
            <input
              id="gEmail" className={styles.input} type="email" value={guardian.email}
              onChange={(e) => setGuardian((g) => ({ ...g, email: e.target.value }))}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="gRel">Relationship</label>
            <select
              id="gRel" className={styles.input} value={guardian.relationship}
              onChange={(e) => setGuardian((g) => ({ ...g, relationship: e.target.value }))}
            >
              <option value="parent">Parent</option>
              <option value="guardian">Legal guardian</option>
              <option value="other">Other</option>
            </select>
          </div>
          <p className={styles.note}>
            We&apos;ll email them a secure link to approve or decline. You can start as soon as they approve.
          </p>
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.actions}>
            <Button
              onClick={() => run(() => requestGuardian(guardian))}
              loading={submitting}
              disabled={!canSend}
            >
              Send request
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Request sent, awaiting the guardian's decision.
  if (state === 'guardian_requested') {
    return (
      <div className={styles.wrap}>
        <Card title="Request sent — waiting on your parent or guardian">
          <p className={styles.body}>
            We&apos;ve emailed your parent or guardian a secure link to approve. Once they say yes,
            you&apos;ll be able to start — you can close this and come back later.
          </p>
        </Card>
      </div>
    );
  }

  // Denied or revoked.
  return (
    <div className={styles.wrap}>
      <Card title="Not available right now">
        <p className={styles.body}>
          {state === 'denied'
            ? 'A parent or guardian declined the request, so this isn’t available yet.'
            : 'You’ve withdrawn from learning. You can rejoin whenever you’re ready.'}
          {state === 'denied' && ' If you think this is a mistake, please reach out to support.'}
        </p>
        {state === 'revoked' && (
          <div className={styles.actions}>
            <Button onClick={() => run(rejoin)} loading={submitting} disabled={submitting}>
              Rejoin
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
